import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  AlertTriangle,
  Check,
  Clock,
  Info,
  Send,
  ShieldOff,
  Sparkles,
  TriangleAlert,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Report, ReportList, ReportType, ReportTypeList, ZoneList } from '@up2d8/contracts';
import { REPORT_MAX_PHOTOS } from '@up2d8/contracts';
import { problemDetail } from '@up2d8/api-client';
import { Card, CardTitle, cn } from '@up2d8/ui';
import { useOffline } from '../../offline/use-offline';
import { absentOrThrow } from '../../shared/home-card';
import { REPORT_TYPE_ICONS } from '../../shared/report-type-icons';
import { useSession } from '../../shared/session';
import { useTenantTheme } from '../../shared/use-tenant-theme';
import { renderAppTabLink, useAppTabs } from './app-tabs';
import { DeskShell } from './desk/desk-shell';
import {
  Crumbs,
  FloorBadge,
  OfflineStrip,
  PhotoPicker,
  SkeletonCards,
  useDayTime,
  useFloorToast,
  type BadgeTone,
  type Photo,
} from './floor/floor-kit';
import { MessagesIcon } from './messages-icon';
import { NotificationBell } from './notification-bell';

/**
 * Melden vanaf de vloer, voor de medewerker (Stap 36), in de vloermodus (VLR-4,
 * `docs/design/UP2D8-melden-replica-spec.md`).
 *
 * Eén scherm: melden boven (op het bureau links), mijn meldingen eronder (rechts). Dat laatste is
 * de helft van de functie — het oude platform had een meldknop zonder afhandeling, en wie niet ziet
 * wat er met zijn melding gebeurt, meldt een tweede keer niet.
 *
 * **De volgorde: soort · foto's · wat zie je · waar · anoniem.** Foto's vóór de beschrijving: wie
 * ernaast staat, neemt eerst een foto. Waar komt na de beschrijving, want die kan de plek al
 * bevatten. En de anoniemschakelaar staat **onderaan**, met de prijs erbij, niet als een
 * onopvallend vinkje bovenaan: anoniem melden kost je de terugkoppeling én de mogelijkheid om je
 * melding terug te vinden, en dat hoort iemand te weten vóór hij verzendt (ADR-0062).
 */

/** Vanaf zeven soorten worden tegels een puzzel; dan een keuzelijst (spec §2). */
const MAX_TILES = 6;

export function AppReportPage() {
  const { t } = useTranslation();
  const session = useSession();
  const tabs = useAppTabs();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { online } = useOffline();
  const { show, element: toast } = useFloorToast();

  useTenantTheme();

  const types = useQuery({
    queryKey: ['me', 'floor-reports', 'types'],
    queryFn: async (): Promise<ReportType[]> => {
      const { data, error } = await session.api.GET('/api/v1/me/floor-reports/types', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as ReportTypeList).items;
    },
  });

  const zones = useQuery({
    queryKey: ['me', 'floor-reports', 'zones'],
    queryFn: async () => {
      const { data, error } = await session.api.GET('/api/v1/me/floor-reports/zones', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as ZoneList).items;
    },
  });

  const mine = useQuery({
    queryKey: ['me', 'floor-reports'],
    queryFn: async (): Promise<Report[]> => {
      const { data, error } = await session.api.GET('/api/v1/me/floor-reports', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as ReportList).items;
    },
  });

  const [typeId, setTypeId] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [anonymous, setAnonymous] = useState(false);

  const selected = (types.data ?? []).find((type) => type.id === typeId) ?? null;
  const anonymousPossible = selected?.anonymousAllowed ?? false;
  const anonymousNow = anonymous && anonymousPossible;
  // Zodra je begint, verdwijnt de onderbalk en staat de knop vast onderaan (spec §2, M1 → M2).
  const started = typeId !== '' || description !== '' || photos.length > 0;
  const ready = typeId !== '' && description.trim().length >= 5;

  const submit = useMutation({
    mutationFn: async () => {
      const { error } = await session.api.POST('/api/v1/me/floor-reports', {
        body: {
          typeId,
          zoneId: zoneId || null,
          description,
          fileIds: photos.map((photo) => photo.fileId),
          anonymous: anonymousNow,
          clientRef: crypto.randomUUID(),
        },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['me', 'floor-reports'] });
      setTypeId('');
      setDescription('');
      setPhotos([]);
      setZoneId('');
      setAnonymous(false);
      show({
        tone: 'ok',
        text: anonymousNow ? t('appReport.sentAnonymous') : t('appReport.sent'),
      });
    },
    // Het formulier blijft ingevuld staan: de fout is van ons, niet van de melder.
    onError: (error) => show({ tone: 'err', text: problemDetail(error) ?? t('appReport.failed') }),
  });

  const blocked = !ready || !online;
  const sendButton = (block: boolean) => (
    <button
      type="button"
      className={cn('fm-btn', blocked ? 'is-disabled' : 'orange', block && 'block')}
      disabled={blocked || submit.isPending}
      onClick={() => submit.mutate()}
      data-testid="report-submit"
    >
      <Send aria-hidden width={20} height={20} />
      {anonymousNow ? t('appReport.submitAnonymous') : t('appReport.submit')}
    </button>
  );
  const why = !online ? (
    <div className="rp-hint">{t('appReport.offlineSubmit')}</div>
  ) : !ready ? (
    <div className="rp-hint">{t('appReport.whyDisabled')}</div>
  ) : null;

  const noTypes = !types.isPending && (types.data ?? []).length === 0;

  return (
    <DeskShell
      tabs={tabs}
      activeKey="home"
      renderLink={renderAppTabLink}
      messages={<MessagesIcon />}
      bell={<NotificationBell />}
      subBar={{
        title: t('appReport.cardTitle'),
        onBack: () => void navigate({ to: '/app' }),
        backLabel: t('floor.back'),
      }}
      mobileFooter={
        started && !noTypes ? (
          <div className="fm-footer">
            {why}
            {sendButton(true)}
          </div>
        ) : undefined
      }
    >
      <div className="fm cf-grid" data-testid="report-page">
        {!online && !noTypes ? (
          <div className="w-full" style={{ gridColumn: '1 / -1' }}>
            <OfflineStrip>{t('appReport.offline')}</OfflineStrip>
          </div>
        ) : null}

        <div className="cf-col">
          <div>
            <Crumbs
              items={[
                { label: t('deskNav.home'), to: '/app' },
                { label: t('appReport.cardTitle') },
              ]}
            />
            {noTypes ? (
              <h1 className="cf-title cf-desk-title">{t('appReport.cardTitle')}</h1>
            ) : (
              <>
                <h1 className="cf-title cf-desk-title">{t('appReport.formTitle')}</h1>
                <div className="fm-big cf-phone-only">{t('appReport.formTitle')}</div>
                <div className="cf-desc">{t('appReport.intro')}</div>
              </>
            )}
          </div>

          {types.isPending ? (
            <div className="rp-tiles" aria-busy="true" aria-label={t('common.loading')}>
              {[0, 1, 2, 3].map((index) => (
                <div key={index} className="sk" style={{ height: 104, borderRadius: 18 }} />
              ))}
            </div>
          ) : noTypes ? (
            <div className="fm-card cf-empty" data-testid="report-not-set-up">
              <div className="fm-hero-icon">
                <AlertTriangle aria-hidden width={32} height={32} />
              </div>
              <div className="fm-title" style={{ fontSize: 18 }}>
                {t('appReport.notSetUpTitle')}
              </div>
              <div className="cf-desc">{t('appReport.notSetUpText')}</div>
            </div>
          ) : (
            <div className="ff-sheet" data-testid="report-form">
              <div className="ff-group" style={{ maxWidth: 720 }}>
                <div className="fm-field">
                  <div className="fm-label" id="report-type-label">
                    <span className="rp-step">1</span> {t('appReport.type')}{' '}
                    <span className="fm-label-opt">{t('appForms.required')}</span>
                  </div>
                  <TypePicker
                    types={types.data ?? []}
                    value={typeId}
                    onChange={(next) => {
                      setTypeId(next);
                      setAnonymous(false);
                    }}
                  />
                  {selected?.description ? (
                    <div className={cn('rp-explain', selected.severe && 'sev')}>
                      {selected.severe ? (
                        <TriangleAlert aria-hidden width={20} height={20} />
                      ) : (
                        <Info aria-hidden width={20} height={20} />
                      )}
                      <div>{selected.description}</div>
                    </div>
                  ) : null}
                </div>

                <div className="fm-field">
                  <div className="fm-label">
                    <span className="rp-step">2</span> {t('appReport.photos')}{' '}
                    <span className="fm-label-opt">{t('appForms.optional')}</span>
                  </div>
                  <PhotoPicker
                    photos={photos}
                    max={REPORT_MAX_PHOTOS}
                    onChange={setPhotos}
                    ownerModule="floor-reports"
                    purpose="floor_report_photo"
                    offline={!online}
                    offlineHint={t('appReport.photoOffline')}
                    testId="report-photo"
                  />
                  {online ? (
                    <div className="fm-hint">
                      {t('appReport.maxPhotos', { count: REPORT_MAX_PHOTOS })}
                    </div>
                  ) : null}
                </div>

                <div className="fm-field">
                  <label className="fm-label" htmlFor="report-description">
                    <span className="rp-step">3</span> {t('appReport.description')}{' '}
                    <span className="fm-label-opt">{t('appForms.required')}</span>
                  </label>
                  <textarea
                    id="report-description"
                    className="fm-input area"
                    value={description}
                    maxLength={2000}
                    placeholder={t('appReport.descriptionPlaceholder')}
                    onChange={(event) => setDescription(event.target.value)}
                    data-testid="report-description"
                  />
                  <div className="cf-count">
                    {t('appReport.count2000', { count: description.length })}
                  </div>
                </div>

                <div className="fm-field">
                  <label className="fm-label" htmlFor="report-zone">
                    <span className="rp-step">4</span> {t('appReport.zone')}{' '}
                    <span className="fm-label-opt">{t('appForms.optional')}</span>
                  </label>
                  <select
                    id="report-zone"
                    className="fm-input"
                    value={zoneId}
                    onChange={(event) => setZoneId(event.target.value)}
                    data-testid="report-zone"
                  >
                    <option value="">{t('appReport.noZone')}</option>
                    {(zones.data ?? []).map((zone) => (
                      <option key={zone.id} value={zone.id}>
                        {zone.name}
                      </option>
                    ))}
                  </select>
                  <div className="fm-hint">{t('appReport.zoneHint')}</div>
                </div>

                {anonymousPossible ? (
                  <div className={cn('rp-anon', anonymous && 'on')}>
                    <label className="rp-anon-row">
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={anonymous}
                        onChange={(event) => setAnonymous(event.target.checked)}
                        data-testid="report-anonymous"
                      />
                      <span className={cn('ff-box', anonymous && 'on')} aria-hidden>
                        {anonymous ? <Check width={18} height={18} strokeWidth={3} /> : null}
                      </span>
                      <span className="rp-anon-label">
                        <ShieldOff aria-hidden width={20} height={20} />
                        {t('appReport.anonymous')}
                      </span>
                    </label>
                    <div className="rp-anon-text">
                      {anonymous ? t('appReport.anonymousOn') : t('appReport.anonymousHint')}
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Telefoon vóór je begint: de knop in de pagina, met de onderbalk eronder (M1). */}
              {!started ? (
                <div className="fm-section cf-phone-only" style={{ gap: 8 }}>
                  {why}
                  {sendButton(true)}
                </div>
              ) : null}
              <div className="ff-desk-actions" style={{ alignItems: 'flex-start' }}>
                {why}
                {sendButton(false)}
              </div>
            </div>
          )}
        </div>

        <aside className="cf-aside" data-testid="my-reports">
          <h2 className="fm-h">
            {t('appReport.mine')}{' '}
            {mine.data ? <span className="fm-h-count">{mine.data.length}</span> : null}
          </h2>
          {mine.isPending ? (
            <SkeletonCards count={1} />
          ) : (mine.data ?? []).length === 0 ? (
            <div className="fm-card">
              <div className="fm-meta">{t('appReport.noneYet')}</div>
            </div>
          ) : (
            <>
              {(mine.data ?? []).map((report) => (
                <MyReport key={report.id} report={report} />
              ))}
            </>
          )}
          <div className="fm-hint">{t('appReport.anonymousNotListed')}</div>
        </aside>
        {toast}
      </div>
    </DeskShell>
  );
}

/**
 * De soort als tegels: twee kolommen, een icoon en de naam. Een ernstige soort heeft een oranje
 * icoon en het woord "Ernstig" — woord plus kleur, nooit alleen kleur.
 */
function TypePicker({
  types,
  value,
  onChange,
}: {
  types: ReportType[];
  value: string;
  onChange: (typeId: string) => void;
}) {
  const { t } = useTranslation();

  if (types.length > MAX_TILES) {
    return (
      <select
        className="fm-input"
        aria-labelledby="report-type-label"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        data-testid="report-type"
      >
        <option value="">{t('appReport.chooseType')}</option>
        {types.map((type) => (
          <option key={type.id} value={type.id}>
            {type.name}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div className="rp-tiles" role="radiogroup" aria-labelledby="report-type-label">
      {types.map((type) => {
        const Icon = REPORT_TYPE_ICONS[type.icon] ?? TriangleAlert;
        const on = type.id === value;
        return (
          <button
            key={type.id}
            type="button"
            role="radio"
            aria-checked={on}
            className={cn('rp-tile', on && 'on')}
            onClick={() => onChange(type.id)}
            data-testid="report-type-tile"
          >
            <span className={cn('cf-roundicon', type.severe ? 'orange' : 'grey')}>
              <Icon aria-hidden width={22} height={22} />
            </span>
            <span className="rp-tile-name">{type.name}</span>
            {type.severe ? (
              <span className="rp-tile-sev">
                <FloorBadge tone="attention">{t('appReport.severe')}</FloorBadge>
              </span>
            ) : null}
            {on ? (
              <span className="rp-tile-check">
                <Check aria-hidden width={16} height={16} strokeWidth={3} />
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

const STATUS: Record<Report['status'], { tone: BadgeTone; icon: typeof Check }> = {
  new: { tone: 'neutral', icon: Sparkles },
  in_progress: { tone: 'attention', icon: Clock },
  resolved: { tone: 'ok', icon: Check },
  rejected: { tone: 'danger', icon: X },
};

function MyReport({ report }: { report: Report }) {
  const { t, i18n } = useTranslation();
  const dayTime = useDayTime();
  const status = STATUS[report.status];
  const StatusIcon = status.icon;
  const day = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { day: '2-digit', month: '2-digit' }).format(
      new Date(iso),
    );

  // De geschiedenis zoals de melder haar mag zien: zonder de interne opmerkingen.
  const history = report.events.filter((event) => event.comment !== '');

  return (
    <article className="fm-card rq" data-testid="my-report">
      <div className="rq-top">
        <div className="cf-listcard-info" style={{ gap: 2 }}>
          <div className="rq-title">
            {report.typeName}
            {report.zoneName ? ` · ${report.zoneName}` : ''}
          </div>
          <div className="fm-meta">{dayTime(report.submittedAt)}</div>
        </div>
        <FloorBadge tone={status.tone} icon={<StatusIcon aria-hidden width={14} height={14} />}>
          {t(`floorReports.status.${report.status}`)}
        </FloorBadge>
      </div>
      <div className="rp-desc whitespace-pre-wrap">{report.description}</div>
      {history.length > 0 ? (
        <div className="rp-history" aria-label={t('appReport.history')}>
          {history.map((event) => (
            <div key={event.id} className="rp-hist">
              <div className="rp-hist-by">
                {[day(event.at), event.actorName].filter(Boolean).join(' · ')}
              </div>
              {event.comment}
            </div>
          ))}
        </div>
      ) : null}
    </article>
  );
}

export function AppReportCard() {
  const { t } = useTranslation();
  const { api } = useSession();

  const types = useQuery({
    queryKey: ['me', 'floor-reports', 'types'],
    queryFn: async (): Promise<ReportType[]> => {
      const { data, error, response } = await api.GET('/api/v1/me/floor-reports/types', {});
      if (error || !data) return absentOrThrow(response, error);
      return (data as ReportTypeList).items;
    },
  });

  if ((types.data ?? []).length === 0) return null;

  return (
    <Card compact data-app-card>
      <Link to="/app/melden" className="flex min-h-12 items-center gap-element">
        <span aria-hidden className="rounded-pill bg-brand-orange-tint p-2 text-brand-orange-deep">
          <AlertTriangle className="h-5 w-5" strokeWidth={2.5} />
        </span>
        <span className="min-w-0 flex-1">
          <CardTitle>{t('appReport.cardTitle')}</CardTitle>
          <span className="block text-small text-text-secondary">{t('appReport.cardHint')}</span>
        </span>
      </Link>
    </Card>
  );
}
